import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LayoutProvider } from './context/LayoutContext';
import { ThemeProvider } from './context/ThemeContext';
import { ShoppingListProvider } from './context/ShoppingListContext';
import { FridgeScanProvider } from './context/FridgeScanContext';
import { BreadcrumbProvider } from './context/BreadcrumbContext';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RecipeList from './pages/RecipeList';
import RecipeDetail from './pages/RecipeDetail';
import RecipeDetailMobile from './pages/RecipeDetail.mobile';
import useIsMobile from './hooks/useIsMobile';
import RecipeEditor from './pages/RecipeEditor';
import Upload from './pages/Upload';
import UserSettings from './pages/UserSettings';
import Layout from './components/Layout';
import AdminLayout from './layouts/AdminLayout';
import AdminDashboard from './pages/admin/Dashboard';
import Users from './pages/admin/Users';
import UserEdit from './pages/admin/UserEdit';
import Recipes from './pages/admin/Recipes';
import RecipeEdit from './pages/admin/RecipeEdit';
import Pages from './pages/admin/Pages';
import ContentEditor from './pages/admin/ContentEditor';
import Modules from './pages/admin/Modules';
import Packages from './pages/admin/Packages';
import Regions from './pages/admin/Regions';
import Corrections from './pages/admin/Corrections';
import Moderation from './pages/admin/Moderation';
import Settings from './pages/admin/Settings';
import AuditLog from './pages/admin/AuditLog';
import Templates from './pages/admin/Templates';
import PageRenderer from './pages/PageRenderer';
import HomePage from './pages/HomePage';
import Dashboard from './pages/Dashboard';
import Wochenplaner from './pages/Wochenplaner';
import MyRecipes from './pages/MyRecipes';
import Impressum from './pages/Impressum';
import Datenschutz from './pages/Datenschutz';
import LegalPage from './pages/LegalPage';
import ShareView from './pages/ShareView';
import Pakete from './pages/Pakete';
import FridgeScan from './pages/FridgeScan';
import Inventar from './pages/Inventar';
import ShoppingListPage from './pages/ShoppingListPage';
import Import from './pages/Import';
import Feedback from './pages/Feedback';
import './App.css';
import './styles/content-v5.css';

function RecipeDetailRoute() {
  const isMobile = useIsMobile();
  return isMobile ? <RecipeDetailMobile /> : <RecipeDetail />;
}

function PrivateRoute({ children }) {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
}

function AdminRoute({ children }) {
  const { user } = useAuth();
  return user && user.is_admin ? children : <Navigate to="/" />;
}

function DashboardRoute() {
  const { user } = useAuth();
  return user ? <Dashboard /> : <Navigate to="/rezepte" />;
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <LayoutProvider>
      <ShoppingListProvider>
      <FridgeScanProvider>
      <BreadcrumbProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Startseite → direkt ins Portal */}
          <Route path="/" element={<Navigate to="/rezepte" replace />} />
          <Route path="/about" element={<HomePage />} />
          <Route path="/s/:token" element={<ShareView />} />

          {/* /demo → Portal weiterleiten */}
          <Route path="/demo" element={<Navigate to="/rezepte" replace />} />
          <Route path="/demo/*" element={<Navigate to="/rezepte" replace />} />

          {/* Öffentliches Portal — kein Login nötig */}
          <Route path="/" element={<Layout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="rezepte" element={<RecipeList />} />
            <Route path="recipe/:id" element={<RecipeDetailRoute />} />
            <Route path="rezept/:slug" element={<RecipeDetailRoute />} />
            <Route path="kuehlschrank" element={<FridgeScan />} />
            <Route path="kuehlschrank/inventar" element={<Inventar />} />
            <Route path="upload" element={<Upload />} />
            <Route path="importieren" element={<Import />} />
            <Route path="feedback" element={<Feedback />} />
            <Route path="impressum" element={<Impressum />} />
            <Route path="datenschutz" element={<Datenschutz />} />
            <Route path="agb" element={<LegalPage slug="agb" title="Allgemeine Geschäftsbedingungen" subtitle="Stand: 2026" />} />
            <Route path="kontakt" element={<LegalPage slug="kontakt" title="Kontakt" />} />
          </Route>

          {/* Private Seiten — Login nötig */}
          <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="woche" element={<Wochenplaner />} />
            <Route path="new" element={<RecipeEditor />} />
            <Route path="edit/:id" element={<RecipeEditor />} />
            <Route path="pakete" element={<Pakete />} />
            <Route path="preise" element={<Pakete />} />
            <Route path="my-recipes" element={<MyRecipes />} />
            <Route path="einstellungen" element={<UserSettings />} />
            <Route path="einkaufsliste" element={<ShoppingListPage />} />
          </Route>

          {/* Custom CMS-Seiten */}
          <Route path="page/:slug" element={<PageRenderer />} />

          {/* Admin-Bereich — eigenes Layout */}
          <Route path="/admin" element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<Users />} />
            <Route path="users/edit/:id" element={<UserEdit />} />
            <Route path="users/new" element={<UserEdit />} />
            <Route path="recipes" element={<Recipes />} />
            <Route path="recipes/edit/:id" element={<RecipeEdit />} />
            <Route path="recipes/new" element={<RecipeEdit />} />
            <Route path="pages" element={<Pages />} />
            <Route path="content/:slug" element={<ContentEditor />} />
            <Route path="modules" element={<Modules />} />
            <Route path="packages" element={<Packages />} />
            <Route path="regions" element={<Regions />} />
            <Route path="corrections" element={<Corrections />} />
            <Route path="moderation" element={<Moderation />} />
            <Route path="templates" element={<Templates />} />
            <Route path="settings" element={<Settings />} />
            <Route path="audit-log" element={<AuditLog />} />
          </Route>
        </Routes>
      </Router>
      </BreadcrumbProvider>
      </FridgeScanProvider>
      </ShoppingListProvider>
      </LayoutProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
